import * as types from '../mutation-types/builder'

const namespaced = true
export const namespace = 'builder'

// state
const state = {
    getEditor: null,
    contentSet: null,
    content: null,
    template: null,

    leftSideOpen: true,
    rightSideOpen: true,

    editorCanvasView: 'default',
    editorSorterActive: false,

    editorLoadingCounter: 0,
    canvasDirty: false,
    canvasChangesCount: 0,
    canvasSelectedComponent: null,
    autosaveEnabled: false,
    autosaved: false,

    viewCanvasGrid: false,

    builderOptions: null,
    editorOptions: null,
    getBuilderApp: null,
    getBuilder: null,
    getServiceManager: null
}

// mutations
const mutations = {
    [types.SET_EDITOR](s, editor) {
        s.getEditor = () => editor
    },
    [types.SET_CONTENT_SET](s, contentSet) {
        s.contentSet = contentSet
    },
    [types.SET_CONTENT](s, content) {
        s.content = content
    },
    [types.SET_TEMPLATE](s, template) {
        s.template = template
    },

    [types.SET_LEFT_SIDE_STATE](s, open) {
        s.leftSideOpen = open
    },
    [types.SET_RIGHT_SIDE_STATE](s, open) {
        s.rightSideOpen = open
    },

    [types.SET_EDITOR_CANVAS_VIEW](s, tag) {
        s.editorCanvasView = tag
    },
    [types.SET_EDITOR_SORTER_ACTIVE](s, active) {
        s.editorSorterActive = active
    },

    [types.INCREASE_EDITOR_LOADING_COUNTER](s) {
        s.editorLoadingCounter++
    },
    [types.DECREASE_EDITOR_LOADING_COUNTER](s) {
        s.editorLoadingCounter--
        if (s.editorLoadingCounter < 0) {
            s.editorLoadingCounter = 0
        }
    },
    [types.SET_CANVAS_CHANGES_COUNT](s, changesCount) {
        s.canvasDirty = changesCount > 0
        s.canvasChangesCount = changesCount
    },
    [types.SET_CANVAS_SELECTED_COMPONENT](s, component) {
        s.canvasSelectedComponent = component
    },
    [types.SET_AUTOSAVE_ENABLED](s, autosaveEnabled) {
        s.autosaveEnabled = autosaveEnabled
    },
    [types.SET_AUTOSAVED](s, autosaved) {
        s.autosaved = autosaved
    },

    [types.SET_VIEW_CANVAS_GRID](s, viewCanvasGrid) {
        s.viewCanvasGrid = viewCanvasGrid
    },

    [types.SET_BUILDER_OPTIONS](s, config) {
        s.builderOptions = config
    },

    [types.SET_EDITOR_OPTIONS](s, config) {
        s.editorOptions = config
    },

    [types.SET_BUILDER_APP](s, app) {
        s.getBuilderApp = () => app
    },
    [types.SET_BUILDER](s, builder) {
        s.getBuilder = () => builder
    },
    [types.SET_SERVICE_MANAGER](s, manager) {
      s.getServiceManager = () => manager
    },
}

// actions
const actions = {
    setEditor({commit}, editor) {
      commit(types.SET_EDITOR, editor)
    },
    setCurrentContentSet({commit}, contentSet) {
        commit(types.SET_CONTENT_SET, contentSet && { ...contentSet })
    },
    setCurrentContent({commit}, content) {
        commit(types.SET_CONTENT, content && { ...content })
    },
    setCurrentTemplate({commit}, template) {
        commit(types.SET_TEMPLATE, template && { ...template })
    },

    openLeftSide({commit}) {
        commit(types.SET_LEFT_SIDE_STATE, true)
    },
    closeLeftSide({commit}) {
        commit(types.SET_LEFT_SIDE_STATE, false)
    },
    openRightSide({commit}) {
        commit(types.SET_RIGHT_SIDE_STATE, true)
    },
    closeRightSide({commit}) {
        commit(types.SET_RIGHT_SIDE_STATE, false)
    },

    setEditorCanvasView({commit}, tag) {
        commit(types.SET_EDITOR_CANVAS_VIEW, tag)
    },
    setEditorSorterActive({commit}, active) {
        commit(types.SET_EDITOR_SORTER_ACTIVE, active)
    },

    increaseEditorLoadingCounter({commit}) {
        commit(types.INCREASE_EDITOR_LOADING_COUNTER)
    },
    decreaseEditorLoadingCounter({commit}) {
        commit(types.DECREASE_EDITOR_LOADING_COUNTER)
    },
    setCanvasChangesCount({commit}, changesCount) {
        commit(types.SET_CANVAS_CHANGES_COUNT, changesCount)
    },
    setCanvasSelectedComponent({commit}, component) {
        commit(types.SET_CANVAS_SELECTED_COMPONENT, component)
    },
    setAutosaveEnabled({commit}, autosaveEnabled) {
        commit(types.SET_AUTOSAVE_ENABLED, autosaveEnabled)
    },
    setAutosaved({commit}, autosaved) {
        commit(types.SET_AUTOSAVED, autosaved)
    },

    setViewCanvasGrid({commit}, viewCanvasGrid) {
        commit(types.SET_VIEW_CANVAS_GRID, viewCanvasGrid)
    },

    setBuilderOptions({commit}, config) {
      commit(types.SET_BUILDER_OPTIONS, config)
    },

    setEditorOptions({commit}, config) {
      commit(types.SET_EDITOR_OPTIONS, config)
    },

    setBuilderApp({commit}, app) {
      commit(types.SET_BUILDER_APP, app)
    },
    setBuilder({commit}, builder) {
      commit(types.SET_BUILDER, builder)
    },
    setServiceManager({commit}, manager) {
      commit(types.SET_SERVICE_MANAGER, manager)
    },
}

// getters
const getters = {
    getServiceManager: store => store.getServiceManager,
    getEditor: store => store.getEditor,

    currentContentSet: store => store.contentSet,
    currentContent: store => store.content,
    currentTemplate: store => store.template,
    contentSetId: store => store.contentSet?.id,
    contentId: store => store.content?.id,

    leftSideOpen: s => s.leftSideOpen,
    rightSideOpen: s => s.rightSideOpen,

    editorCanvasView: s => s.editorCanvasView,
    editorSorterActive: s => s.editorSorterActive,

    editorLoadingCounter: s => s.editorLoadingCounter,
    canvasDirty: s => s.canvasDirty,
    canvasChangesCount: s => s.canvasChangesCount,
    canvasSelectedComponent: s => s.canvasSelectedComponent,
    autosaveEnabled: s => s.autosaveEnabled,
    autosaved: s => s.autosaved,
    canSafelyUnloadContent: s => !s.canvasDirty || s.autosaveEnabled,

    viewCanvasGrid: s => s.viewCanvasGrid,
    storedBuilderOptions: store => store.builderOptions,
    getBuilderOptions: store => (key, defaultValue) => {
      if (key) {
        return _.get(store.builderOptions, key, defaultValue)
      }
      return store.builderOptions
    },
    getEditorOptions: store => (key, defaultValue) => {
      if (key) {
        return _.get(store.editorOptions, key, defaultValue)
      }
      return store.editorOptions
    },
    storedEditorOptions: store => store.editorOptions,
    getBuilderApp: store => store.getBuilderApp,
    getBuilder: store => store.getBuilder
}

export default {
    namespaced,
    namespace,
    state,
    mutations,
    actions,
    getters
}
